import { useMutation, useQuery } from "@tanstack/vue-query";
import { clone, omit } from "radash";

interface Session {
  notary_details_id: string;
  date_time: string;
  docs_num: string;
  session_status: string;
  apt_duration: number;
  clients: string;
  notaries: string;
}

export const useSession = defineStore("session", () => {
  const route = useRoute();
  const { $axios, $reportError } = useNuxtApp();
  const toast = useMessage();

  const model_id = ref("");
  const session = ref<any>(null);
  const create = ref({});
  const clients = ref([]);
  const associates = ref([]);
  const witnesses = ref([]);
  const users = ref([]);
  const metadata = ref({});
  const isLockingDocument = ref(false);

  const { mutate: updateMeta } = useMutation({
    mutationFn: async (data: any) => {
      return await $axios
        .put(`/v1/sessions/${route.params.id}`, {
          ...metadata.value,
          ...data,
        })
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      refetch();
    },
    onError: (error) => {
      $reportError("Session Store Error", error);
    },
  });

  const { data, refetch, isFetching, suspense } = useQuery({
    queryKey: ["session", route.params.id],
    queryFn: async () => {
      return await $axios.get(`/v1/sessions/${route.params.id}`).then((res) => {
        session.value = clone(
          omit(res.data, [
            "witnesses",
            "associates",
            "clients",
            "users",
            "metadata",
          ]),
        );
        witnesses.value = res.data.witnesses;
        associates.value = res.data.associates;
        clients.value = res.data.clients;
        users.value = res.data.users;
        model_id.value = route.params.user_id as string;
        metadata.value = res.data.metadata;
        return res.data;
      });
    },
    enabled: computed(() => Boolean(route.params.id)),
  });

  /**
   * This method clears the state information.
   */
  const clearSession = async () => {
    if (witnesses) {
      witnesses.value = [];
    }

    if (associates) {
      associates.value = [];
    }

    if (clients) {
      clients.value = [];
    }

    if (users) {
      users.value = [];
    }

    if (model_id) {
      model_id.value = "";
    }
    
    if (metadata) {
      metadata.value = {};
    }
  }

  const setIsLockingDocument = (isLocking: boolean) => {
    isLockingDocument.value = isLocking;
  }

  /**
   * This method makes a request to the server and reloads the document for other
   * users that are connected to the session. This is done via a websocket request
   */
  const requestDocumentReloadForOthers = (sessionId: string, mediaId: string) => {
    $axios.post(`/v1/sessions/${sessionId}/files/${mediaId}/reload-document`).catch((err) => {
      toast.message({
        type: 'error',
        message: err.response.data.message,
        time: 10000,
      })
      $reportError("Document Reload Request for other users in the session failed.", err);
    });
  }

  return {
    updateMeta,
    model_id,
    session,
    clients,
    create,
    associates,
    data,
    witnesses,
    users,
    metadata,
    refetch,
    suspense,
    isFetching,
    clearSession,
    isLockingDocument,
    setIsLockingDocument,
    requestDocumentReloadForOthers,
  };
});
